import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, getFirestore, limit, orderBy, query } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { useStripePayments, StripePayment } from '../../contexts/StripePaymentsContext';

// ---------------------------------------------------------------------------
// "Direct Stripe payments — needs review" panel for the Website Submissions
// page. Lists each captured (pending) Stripe payment with a suggested invoice
// match and an Apply / Dismiss action. Applying records a real ledger payment
// against the chosen invoice, dated to the Stripe payment date.
// ---------------------------------------------------------------------------

interface OpenInvoice {
  id: string;
  invoiceNumber: string;
  contactName: string;
  contactEmail: string;
  amountDue: number;
  dueDate: string;
}

function fmtMoney(n: number, currency = 'AUD'): string {
  return `${currency === 'AUD' ? '$' : ''}${n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(ymd: string): string {
  if (!ymd) return '';
  const d = new Date(`${ymd}T12:00:00`);
  if (Number.isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Does `invNum` appear in `cf` as a whole token (bounded by non-alphanumerics
 *  or the string ends)? Prevents "INV-5" matching inside "INV-50". */
function containsInvoiceToken(cf: string, invNum: string): boolean {
  if (!invNum) return false;
  const esc = invNum.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(^|[^a-z0-9])${esc}([^a-z0-9]|$)`, 'i').test(cf);
}

/**
 * Best-effort STRONG suggested invoice for a captured payment (owner confirms).
 * Only returns a suggestion when the signal is trustworthy enough to preselect:
 *   1. same customer email AND matching amount,
 *   2. the invoice number appears as a WHOLE TOKEN in a Stripe custom field
 *      (longest / most-specific number wins), or
 *   3. exactly one open invoice whose balance equals the paid amount.
 * A weak email-only (amount-agnostic) match deliberately returns '' — a $50
 * payment must never confidently preselect a customer's lone $5000 invoice.
 */
function suggestInvoiceId(p: StripePayment, invoices: OpenInvoice[]): string {
  const amt = p.amount;
  const email = (p.email || '').toLowerCase().trim();
  const near = (due: number) => Math.abs((due || 0) - amt) < 0.01;

  // 1. Same customer email AND matching amount — strongest.
  const byEmail = email ? invoices.filter((i) => i.contactEmail.toLowerCase() === email) : [];
  const emailAmt = byEmail.find((i) => near(i.amountDue));
  if (emailAmt) return emailAmt.id;

  // 2. Invoice number quoted as a whole token in a Stripe custom field.
  const cf = (p.customFields || []).map((f) => f.value).join(' ').toLowerCase();
  if (cf) {
    const numMatches = invoices
      .filter((i) => containsInvoiceToken(cf, i.invoiceNumber))
      .sort((a, b) => b.invoiceNumber.length - a.invoiceNumber.length); // most specific first
    if (numMatches.length > 0) return numMatches[0].id;
  }

  // 3. Exactly one open invoice whose balance equals the paid amount.
  const amtMatches = invoices.filter((i) => near(i.amountDue));
  if (amtMatches.length === 1) return amtMatches[0].id;

  // Weak signals (email-only, amount ignored) → no confident preselect.
  return '';
}

const StripePaymentRow: React.FC<{
  payment: StripePayment;
  invoices: OpenInvoice[];
  invoicesLoading: boolean;
}> = ({ payment, invoices, invoicesLoading }) => {
  const { applyPayment, dismissPayment } = useStripePayments();
  const suggested = useMemo(() => suggestInvoiceId(payment, invoices), [payment, invoices]);
  const [selected, setSelected] = useState<string>('');
  const [busy, setBusy] = useState<'apply' | 'dismiss' | null>(null);

  // Preselect the suggestion once invoices arrive (unless the user already chose).
  useEffect(() => {
    if (!selected && suggested) setSelected(suggested);
  }, [suggested]); // eslint-disable-line react-hooks/exhaustive-deps

  const cfHint = (payment.customFields || []).map((f) => f.value).filter(Boolean).join(' · ');
  const isTest = payment.testMode === true;

  const handleApply = async () => {
    if (isTest) { toast.warning('This is a test-mode payment — it can\'t be applied to a real invoice.'); return; }
    if (!selected) { toast.warning('Pick an invoice to apply this payment to.'); return; }
    setBusy('apply');
    const res = await applyPayment(payment.id, selected);
    setBusy(null);
    if (res.ok) {
      toast.success(`✅ Applied ${fmtMoney(payment.amount, payment.currency)} to ${res.invoiceNumber || 'invoice'}`);
    } else {
      toast.error(res.error || 'Could not apply payment');
    }
  };

  const handleDismiss = async () => {
    setBusy('dismiss');
    const res = await dismissPayment(payment.id);
    setBusy(null);
    if (!res.ok) toast.error(res.error || 'Could not dismiss payment');
  };

  return (
    <div className="d-flex flex-column flex-lg-row align-items-lg-center gap-3 bg-body rounded border border-gray-300 p-4">
      {/* Payer + amount */}
      <div className="flex-grow-1">
        <div className="d-flex align-items-center gap-2 mb-1">
          <span className="fw-bold text-dark fs-6">{fmtMoney(payment.amount, payment.currency)}</span>
          {payment.testMode && <span className="badge badge-light-danger fs-8">TEST</span>}
        </div>
        <div className="text-muted fs-8">
          {[payment.name, payment.email].filter(Boolean).join(' · ') || 'Unknown payer'}
        </div>
        <div className="text-muted fs-8">
          Paid {fmtDate(payment.paidAt)}
          {cfHint && <span className="ms-2 badge badge-light-info fs-9">{cfHint}</span>}
        </div>
      </div>

      {/* Invoice picker (test-mode payments can't be applied to the books) */}
      <div style={{ minWidth: 260 }}>
        {isTest ? (
          <div className="text-muted fs-8 fst-italic">
            Test-mode payment — not applicable to your books. Dismiss it once you've verified the flow.
          </div>
        ) : (
          <>
            <select
              className="form-select form-select-sm"
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              disabled={invoicesLoading || busy !== null}
            >
              <option value="">
                {invoicesLoading ? 'Loading invoices…' : 'Select an invoice…'}
              </option>
              {invoices.map((inv) => (
                <option key={inv.id} value={inv.id}>
                  {inv.invoiceNumber} — {inv.contactName || inv.contactEmail || '—'} — {fmtMoney(inv.amountDue)}
                  {inv.id === suggested ? '  ★' : ''}
                </option>
              ))}
            </select>
            {suggested && selected === suggested && (
              <div className="text-success fs-9 mt-1">★ Suggested match</div>
            )}
          </>
        )}
      </div>

      {/* Actions */}
      <div className="d-flex gap-2">
        <button
          className="btn btn-sm btn-primary text-nowrap"
          onClick={handleApply}
          disabled={busy !== null || !selected || isTest}
        >
          {busy === 'apply'
            ? <><span className="spinner-border spinner-border-sm me-1"></span>Applying…</>
            : <><i className="fas fa-check me-1"></i>Apply</>}
        </button>
        <button
          className="btn btn-sm btn-light"
          onClick={handleDismiss}
          disabled={busy !== null}
          title="Not invoice-related"
        >
          {busy === 'dismiss'
            ? <span className="spinner-border spinner-border-sm"></span>
            : 'Dismiss'}
        </button>
      </div>
    </div>
  );
};

const StripePaymentsReviewPanel: React.FC = () => {
  const { pending } = useStripePayments();
  const [invoices, setInvoices] = useState<OpenInvoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  // Re-load open invoices whenever the set of pending payments changes — i.e. on
  // a new capture AND after an apply/dismiss (the applied invoice is now paid,
  // so its stale balance must not drive a suggestion or stay in the picker).
  const pendingKey = pending.map((p) => p.id).sort().join(',');
  useEffect(() => {
    if (pending.length === 0) return;
    let cancelled = false;
    setInvoicesLoading(true);
    (async () => {
      try {
        const db = getFirestore();
        const snap = await getDocs(query(collection(db, 'invoices'), orderBy('dueDate', 'desc'), limit(2000)));
        if (cancelled) return;
        const open: OpenInvoice[] = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() as any) }))
          .filter((inv) => (inv.status === 'APPROVED' || inv.status === 'SENT') && (Number(inv.amountDue) || 0) > 0)
          .map((inv) => ({
            id: inv.id,
            invoiceNumber: String(inv.invoiceNumber || ''),
            contactName: String(inv.contactName || ''),
            contactEmail: String(inv.contactEmail || ''),
            amountDue: Number(inv.amountDue) || 0,
            dueDate: String(inv.dueDate || ''),
          }));
        setInvoices(open);
      } catch (err) {
        console.warn('[StripePaymentsReviewPanel] invoice load failed:', err);
      } finally {
        if (!cancelled) setInvoicesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [pendingKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (pending.length === 0) return null;

  return (
    <div className="card mb-5 border border-primary border-dashed">
      <div className="card-header border-0 pt-4 pb-0 d-flex align-items-center">
        <div className="symbol symbol-40px me-3">
          <div className="symbol-label bg-light-primary">
            <i className="fab fa-stripe-s text-primary fs-3"></i>
          </div>
        </div>
        <div>
          <div className="fw-bolder text-dark fs-5">Direct Stripe payments — needs review</div>
          <div className="text-muted fs-8">
            Paid via the "Pay with Stripe" link on an invoice. Apply each to an invoice to record it in your books.
          </div>
        </div>
        <span className="badge badge-primary ms-auto fs-7">{pending.length}</span>
      </div>
      <div className="card-body pt-4">
        <div className="d-flex flex-column gap-3">
          {pending.map((p) => (
            <StripePaymentRow key={p.id} payment={p} invoices={invoices} invoicesLoading={invoicesLoading} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default StripePaymentsReviewPanel;
